import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  Agendamiento,
  AgendamientosPagingRes,
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchAgendamientos = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetAgendamientosParams> = {}) => {
  return useQuery({
    queryKey: ['agendamientos', ...Object.values(params ?? {})],
    queryFn: () => getAgendamientos(params),
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};

export const useGetAgendamiento = (id: string) => {
  return useQuery({
    queryKey: ['agendamiento', id],
    queryFn: () => getAgendamiento(id),
    retry: false,
  });
};

export const useCreateAgendamiento = <T>({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: CreateAgendamientoParams<T>) =>
      createAgendamiento(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agendamientos'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Agendamiento creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateAgendamiento = <T>({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: UpdateAgendamientoParams<T>) =>
      updateAgendamiento(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agendamientos'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Agendamiento actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteAgendamiento = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: deleteAgendamiento,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agendamientos'] });
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetAgendamientosParams = Partial<Agendamiento> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;

  identificacion?: string;

  fecha_creacion_after?: string | null;
  fecha_creacion_before?: string | null
  contrato_estado_contrato?: string | null;
};
export type CreateAgendamientoParamsForm = Omit<
  Agendamiento,
  'id_agendamiento'
>;
export type CreateAgendamientoParams<T> = T;
export type UpdateAgendamientoParams<T> = {
  id: string;
  data: T;
};

export const getAgendamientos = async (params?: GetAgendamientosParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<AgendamientosPagingRes>(
    `${urlBackend}api/v1/agendamiento/?${queryParams}`,
    true
  );
};

export const getAgendamiento = async (id: string) => {
  try {
    return await get<Agendamiento>(
      `${urlBackend}api/v1/agendamiento/${id}/`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createAgendamiento = async <T>(
  data: CreateAgendamientoParams<T>
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Agendamiento>(`${urlBackend}api/v1/agendamiento/`, data, true);
};

export const updateAgendamiento = async <T>({
  id,
  data,
}: UpdateAgendamientoParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Agendamiento>(
    `${urlBackend}api/v1/agendamiento/${id}/`,
    data,
    true
  );
};

export const deleteAgendamiento = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await remove<Agendamiento>(
    `${urlBackend}api/v1/agendamiento/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
